import { z } from 'zod';
import { Ulid } from './common.js';
/**
 * Hardware verification (plan §15).
 *
 * BoardFlight reads an APK's package id and nothing more, so nothing upstream can say whether
 * a build will install. A runner on the developer's own network can: it claims a queued build, installs
 * and launches it on a real Board, and reports what happened. The build then carries an
 * answer instead of a hope.
 */
export const VerificationStatus = z.enum(['queued', 'running', 'passed', 'failed', 'expired']);
/** What a runner says about itself when claiming work. */
export const ClaimVerificationRequest = z.object({
    /** Shown in the dashboard so a failure can be traced to a machine. */
    runnerName: z.string().trim().min(1).max(120),
    boardModel: z.string().trim().max(120).optional(),
    boardOs: z.string().trim().max(60).optional(),
    /** Only claim work for this app. Useful when a Board is dedicated to one game. */
    appId: Ulid.optional(),
});
export const VerificationJob = z.object({
    id: Ulid,
    buildId: Ulid,
    appId: Ulid,
    appName: z.string(),
    packageName: z.string(),
    platform: z.string(),
    versionName: z.string(),
    versionCode: z.number().int(),
    size: z.number().int(),
    sha256: z.string(),
    /**
     * Proof that this runner holds the job. Issued fresh on every claim and required when
     * reporting, so a runner that slept past its lease cannot report on work somebody else
     * has since taken over.
     */
    leaseId: Ulid,
    /** Seconds before the lease lapses and the job is offered to another runner. */
    leaseSeconds: z.number().int(),
});
export const VerificationResultRequest = z.object({
    /** The `leaseId` from the claim. A report without the current lease is refused. */
    leaseId: Ulid,
    passed: z.boolean(),
    /** Board's response, verbatim. The single most useful field when something fails. */
    boardMessage: z.string().max(8000).optional(),
    /** Last lines of the app's log, for a build that installs but dies on launch. */
    logTail: z.string().max(20_000).optional(),
});
export const Verification = z.object({
    id: Ulid,
    buildId: Ulid,
    status: VerificationStatus,
    runnerName: z.string().nullable(),
    boardModel: z.string().nullable(),
    boardOs: z.string().nullable(),
    boardMessage: z.string().nullable(),
    logTail: z.string().nullable(),
    finishedAt: z.number().int().nullable(),
    createdAt: z.number().int(),
});
/**
 * The newest check on a build, as it appears in a build list.
 *
 * Without the log tail: a list of twenty builds would otherwise carry twenty 15 KB logs, and
 * the log is only ever read one build at a time from `/v1/builds/:id/verifications`.
 */
export const VerificationSummary = Verification.omit({ logTail: true });
//# sourceMappingURL=verification.js.map